import React, { useMemo } from "react";
import { Box, Text, Flex, Divider } from "@chakra-ui/react";
import { ConditionGroup } from "stores/data/insightAlerts/insightAlertModel";
import { IEnvironmentalVariable } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { getUnitsMeasurement } from "util/getUnitsByEnvironmentalVariableId/getUnitsByEnvironmentalVariableId";
import i18n from "../../../i18n";

interface RiskTooltipContentProps {
  t: (key: string) => string;
  conditions?: ConditionGroup[];
  environmentalVariables?: IEnvironmentalVariable[];
  /** Translation key for the risk-specific description */
  descriptionKey?: string;
}

/**
 * Get the display name for an environmental variable
 */
const getVariableName = (
  variableId: number,
  variableName: string,
  environmentalVariables?: IEnvironmentalVariable[],
): string => {
  // Try to find the variable in the list to get its proper name
  const variable = environmentalVariables?.find((v) => v.id === variableId);
  if (variable) {
    return variable.name;
  }
  // Fallback to the name from the condition
  return variableName || `Variable ${variableId}`;
};

/**
 * Format a comparison operator for display
 */
const formatOperator = (operator: string): string => {
  switch (operator) {
    case ">":
      return ">";
    case ">=":
      return "≥";
    case "<":
      return "<";
    case "<=":
      return "≤";
    case "=":
      return "=";
    default:
      return operator;
  }
};

/**
 * Format a single condition for display
 */
const formatCondition = (
  condition: {
    environmentalVariableId: number;
    environmentalVariable: string;
    comparisonOperator: string;
    value: number;
  },
  environmentalVariables?: IEnvironmentalVariable[],
): string => {
  const variableName = getVariableName(
    condition.environmentalVariableId,
    condition.environmentalVariable,
    environmentalVariables,
  );
  const operator = formatOperator(condition.comparisonOperator);
  const unit = getUnitsMeasurement(
    condition.environmentalVariableId,
    i18n.language as "pt-BR" | "en",
  );

  return `${variableName} ${operator} ${condition.value}${
    unit ? ` ${unit}` : ""
  }`;
};

/**
 * Render a group of conditions with operators between them
 */
const ConditionGroupBox: React.FC<{
  group: ConditionGroup;
  environmentalVariables?: IEnvironmentalVariable[];
  t: (key: string) => string;
}> = ({ group, environmentalVariables, t }) => {
  const conditionTexts = group.conditions.map((condition) =>
    formatCondition(
      condition as {
        environmentalVariableId: number;
        environmentalVariable: string;
        comparisonOperator: string;
        value: number;
      },
      environmentalVariables,
    ),
  );

  // Join conditions with the operator (OR/AND)
  const operatorText =
    group.conditionOperator?.toUpperCase() === "OR"
      ? t("or").toUpperCase()
      : t("and").toUpperCase();

  return (
    <Box
      bg="#F4F4F5"
      border="1px solid #D4D4D8"
      borderRadius="8px"
      px={2}
      py="6px"
    >
      <Flex direction="column" gap="2px">
        {conditionTexts.map((text, index) => (
          <Text key={index} fontSize="12px" color="#71717A" lineHeight="20px">
            {text}
            {index < conditionTexts.length - 1 && (
              <Text as="span" color="#0884cc" fontWeight="500">
                {" "}
                {operatorText}
              </Text>
            )}
          </Text>
        ))}
      </Flex>
    </Box>
  );
};

/**
 * Divider with text between groups
 */
const GroupDivider: React.FC<{ text: string }> = ({ text }) => (
  <Flex align="center" gap={2} w="100%">
    <Divider borderColor="#D1D9E2" flex={1} />
    <Text
      fontSize="10px"
      fontWeight="600"
      color="#4A6075"
      textTransform="uppercase"
      letterSpacing="1px"
    >
      {text}
    </Text>
    <Divider borderColor="#D1D9E2" flex={1} />
  </Flex>
);

export const RiskTooltipContent: React.FC<RiskTooltipContentProps> = ({
  t,
  conditions,
  environmentalVariables,
  descriptionKey,
}) => {
  const hasConditions = useMemo(() => {
    return (
      conditions &&
      conditions.length > 0 &&
      conditions.some(
        (group) => group.conditions && group.conditions.length > 0,
      )
    );
  }, [conditions]);

  // Get the group operator between groups (from the first group that has one)
  const groupOperatorText = useMemo(() => {
    if (!conditions || conditions.length < 2) return "";
    const firstGroupWithOperator = conditions.find((g) => g.groupOperator);
    const operator = firstGroupWithOperator?.groupOperator?.toUpperCase();
    return operator === "OR" ? t("or").toUpperCase() : t("and").toUpperCase();
  }, [conditions, t]);

  return (
    <Box
      bg="white"
      borderRadius="8px"
      boxShadow="0px 2px 8px rgba(0, 0, 0, 0.25)"
      p="10px"
      minW="260px"
      maxW="300px"
    >
      {/* About the risk section */}
      <Box mb={4}>
        <Text fontSize="12px" fontWeight="700" color="#242E38" mb={2}>
          {t("aboutRisk")}
        </Text>
        <Text fontSize="12px" color="#4A6075" lineHeight="16px">
          {descriptionKey
            ? t(descriptionKey)
            : t("aboutRiskGenericDescription")}
        </Text>
      </Box>

      <Divider borderColor="#D1D9E2" mb={4} />

      {/* Event conditions section */}
      <Text fontSize="12px" fontWeight="700" color="#242E38" mb={3}>
        {t("eventConditions")}
      </Text>

      {hasConditions ? (
        <Flex direction="column" gap="4px">
          {conditions!.map((group, groupIndex) => (
            <React.Fragment key={group.id || groupIndex}>
              {group.conditions && group.conditions.length > 0 && (
                <>
                  <ConditionGroupBox
                    group={group}
                    environmentalVariables={environmentalVariables}
                    t={t}
                  />
                  {groupIndex < conditions!.length - 1 &&
                    conditions![groupIndex + 1]?.conditions?.length > 0 && (
                      <GroupDivider text={groupOperatorText} />
                    )}
                </>
              )}
            </React.Fragment>
          ))}
        </Flex>
      ) : (
        <Text fontSize="12px" color="#71717A" fontStyle="italic">
          {t("noConditionsAvailable")}
        </Text>
      )}
    </Box>
  );
};
